/**
 * Top-bar search input.
 *
 * For authenticated callers the search hits the backend
 * (`GET /api/v1/smt/estates?q=…`) so it spans the WHOLE representative set —
 * not just the listing page that happens to be loaded. This is what lets an
 * admin / level≥3 user find a toteutunut kauppa (closed/sold deal) by name or
 * kiinteistötunnus even though those rows are pushed past the listing's
 * `limit`/`created_at DESC` window. The backend applies the same level/region
 * gating as the listing, including IncludeClosedSold for admin/level≥3.
 *
 * For anonymous (level=0) visitors the backend redacts name/area and ignores
 * `q` (anti-enumeration), so we keep the original client-side substring search
 * over the already-loaded redacted rows.
 *
 * Shows up to 8 matches in a dropdown (`#searchResults`); clicking a match
 * opens the corresponding estate detail.
 */
import { listEstates } from '../api/estates.ts';
import { currentUser } from '../state/session.ts';
import type { Estate, AnonEstate } from '../types/api.ts';

export interface SearchOptions {
  /** Source of estates to search. Recomputed on every keystroke so the
   *  freshest listing data is used. Used for anonymous (level=0) visitors
   *  whose backend search is disabled. */
  rows: () => Array<Estate | AnonEstate>;
  onSelect: (estateId: number) => void;
}

const MAX_RESULTS = 8;
const DEBOUNCE_MS = 250;

export function bindSearch(opts: SearchOptions): void {
  const input = document.getElementById('searchInput') as HTMLInputElement | null;
  const results = document.getElementById('searchResults') as HTMLDivElement | null;
  if (!input || !results) return;

  let lastQuery = '';
  let debounceTimer: ReturnType<typeof setTimeout> | undefined;
  // Monotonic token so a slow backend response can't overwrite a newer one.
  let reqSeq = 0;

  const paintMatches = (matches: Array<Estate | AnonEstate>): void => {
    if (!matches.length) {
      results.innerHTML = `<div class="search-result-empty">—</div>`;
      results.style.display = 'block';
      return;
    }
    results.innerHTML = matches
      .map((r) => {
        const full = r as Estate;
        const id = full.property_id || `#${r.id}`;
        const name = full.name || '—';
        const kunta = full.municipality || (r as AnonEstate).region || '';
        return `
          <button class="search-result" data-id="${r.id}">
            <span class="search-result-id">${escapeHtml(id)}</span>
            <span class="search-result-name">${escapeHtml(name)}</span>
            <span class="search-result-loc">${escapeHtml(String(kunta))}</span>
          </button>
        `;
      })
      .join('');
    results.style.display = 'block';

    results.querySelectorAll<HTMLButtonElement>('.search-result').forEach((btn) => {
      btn.addEventListener('click', () => {
        const id = Number(btn.dataset.id);
        if (Number.isFinite(id)) {
          input.value = '';
          results.innerHTML = '';
          results.style.display = 'none';
          opts.onSelect(id);
        }
      });
    });
  };

  const renderClientSide = (q: string): void => {
    const ql = q.toLowerCase();
    const matches = opts.rows().filter((r) => match(r, ql)).slice(0, MAX_RESULTS);
    paintMatches(matches);
  };

  const renderBackend = (q: string): void => {
    const seq = ++reqSeq;
    listEstates({ q, limit: MAX_RESULTS })
      .then((resp) => {
        if (seq !== reqSeq) return; // a newer query superseded this one
        if (input.value.trim() !== q) return; // input changed/cleared
        paintMatches(resp.data || []);
      })
      .catch(() => {
        if (seq !== reqSeq) return;
        // Fall back to whatever is already loaded so the box stays useful.
        renderClientSide(q);
      });
  };

  const render = (q: string): void => {
    if (!q) {
      results.innerHTML = '';
      results.style.display = 'none';
      return;
    }
    if (currentUser()) renderBackend(q);
    else renderClientSide(q);
  };

  input.addEventListener('input', () => {
    const q = input.value.trim();
    if (q === lastQuery) return;
    lastQuery = q;
    if (debounceTimer) clearTimeout(debounceTimer);
    if (!q) {
      render('');
      return;
    }
    debounceTimer = setTimeout(() => render(q), DEBOUNCE_MS);
  });

  // Hide results when clicking outside the search box.
  document.addEventListener('click', (ev) => {
    const tgt = ev.target as HTMLElement;
    if (!tgt.closest('.search-section')) {
      results.style.display = 'none';
    }
  });

  // Re-show results when refocusing the input with a non-empty value.
  input.addEventListener('focus', () => {
    if (input.value.trim()) render(input.value.trim());
  });
}

function match(r: Estate | AnonEstate, ql: string): boolean {
  const full = r as Estate;
  const fields = [full.property_id, full.name, full.municipality, String(r.region || '')];
  return fields.some((f) => typeof f === 'string' && f.toLowerCase().includes(ql));
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
}
